import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { toastErrors } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { toastCaller } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { SchemaDocumentRef, templatesModel } from '@/entities/templates';

import {
  OrderableDocument,
  UseOrderableDocumentsOptions,
  UseOrderableDocumentsResult,
} from '../config';

const hasSeriesId = (
  document: SchemaDocumentRef,
): document is OrderableDocument => Boolean(document.template_series_id);

export const useOrderableDocuments = ({
  documents,
  schemaId,
}: UseOrderableDocumentsOptions): UseOrderableDocumentsResult => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector(
    organizationModel.selectors.selectRelevantOrganizationID,
  );

  const [failedSchemaId, setFailedSchemaId] = useState<string>();
  const [loadAttempt, setLoadAttempt] = useState(0);

  const schema = useAppSelector((state) =>
    schemaId
      ? templatesModel.selectors.selectEncounterSchemaById(schemaId)(state)
      : undefined,
  );
  const isSchemaLoaded = Boolean(schema);

  useEffect(() => {
    if (schemaId && !isSchemaLoaded) {
      void dispatch(
        templatesModel.fetchEncounterSchemaById({
          id: schemaId,
          organizationId,
        }),
      )
        .unwrap()
        .catch(() => {
          setFailedSchemaId(schemaId);
          toastCaller({
            type: 'error',
            heading: formatMessage(toastErrors.errorHeading),
            message: formatMessage(toastErrors.defaultError),
          });
        });
    }
  }, [schemaId, isSchemaLoaded, loadAttempt, organizationId]);

  const retryLoad = () => {
    setFailedSchemaId(undefined);
    setLoadAttempt((attempt) => attempt + 1);
  };

  if (!schema) {
    return {
      hasLoadError: failedSchemaId === schemaId,
      orderableDocuments: [],
      retryLoad,
    };
  }

  const orderedSeriesIds = new Set(
    documents.map((document) => document.template_series_id),
  );

  const orderableDocuments = schema.documents
    .filter(hasSeriesId)
    .filter(
      (document) =>
        document.inclusion === 'on_demand' &&
        !orderedSeriesIds.has(document.template_series_id),
    );

  return {
    hasLoadError: false,
    orderableDocuments,
    retryLoad,
  };
};
